import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import './AbstractVisual.scss'

const AbstractScene = lazy(() => import('./AbstractScene'))

const DEFAULT_COLORS = ['#3a2a8c', '#e7ff02', '#a855f7']

const supportsWebGL = () => {
  try {
    const canvas = document.createElement('canvas')
    return Boolean(window.WebGLRenderingContext && (canvas.getContext('webgl2') || canvas.getContext('webgl')))
  } catch (err) {
    return false
  }
}

// A WebGL failure (lost context, driver blocklist) must never take the page
// down with it — the gradient backdrop simply stays on its own.
class SceneBoundary extends React.Component {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch() {}
  render() {
    return this.state.failed ? null : this.props.children
  }
}

/**
 * Decorative 3D artwork: a glossy iridescent shape in a dark panel.
 *
 * Purely decorative — it carries no content, so nothing on the page ever
 * waits for it. The dark gradient backdrop is plain CSS and shows
 * immediately; the WebGL scene (a separate, lazily loaded chunk) fades in
 * over it once ready. The scene is skipped entirely when WebGL is missing
 * or the page is rendered in the background (prerender, background tabs),
 * and it stops animating while scrolled out of view or for visitors who
 * prefer reduced motion.
 *
 * shape   'ribbon' | 'weave' | 'loop' | 'orb' | 'ring'
 * colors  [surface, light 1, light 2] — hex strings
 */
const AbstractVisual = ({ shape = 'ribbon', colors = DEFAULT_COLORS, className = '' }) => {
  const ref = useRef(null)
  const reduced = useReducedMotion()
  const [enabled] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'visible' && supportsWebGL()
  )
  const [inView, setInView] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!enabled || !ref.current) return undefined
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      rootMargin: '200px 0px',
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [enabled])

  // Mount the scene the first time the panel comes near the viewport, then
  // keep it (re-creating a WebGL context on every scroll is expensive).
  useEffect(() => {
    if (inView) setReady(true)
  }, [inView])

  return (
    <div
      ref={ref}
      className={`abstract-visual ${className}`}
      style={{ '--av-a': colors[1], '--av-b': colors[2], '--av-c': colors[0] }}
      aria-hidden='true'
    >
      <div className='abstract-visual-glow' />
      {enabled && ready && (
        <SceneBoundary>
          <Suspense fallback={null}>
            <div className='abstract-visual-stage'>
              <AbstractScene shape={shape} colors={colors} animate={!reduced} active={inView} />
            </div>
          </Suspense>
        </SceneBoundary>
      )}
    </div>
  )
}

export default AbstractVisual
